import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { HorizontalEqualLayout, HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { Data1, H3, Text1, TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import { historyData } from '../../common/data/HistoryData';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import { AnimatedWrapper } from '../base/AnimatedWrapper';

interface LineItemProps {
  index: string;
  description: string;
  showTopLine?: boolean;
}

const LineItem: React.FC<LineItemProps> = ({ index, description, showTopLine = true }) => {
  const { isMobile } = useDeviceType();
  return (
    <VerticalLayout wrapContents={true}>
      {showTopLine && <VerticalLayout height={1} backgroundColor="#00000044" />}
      <BTBox height={isMobile ? 24 : 14} />
      <HorizontalLayout wrapContents={true} style={{ alignContent: 'start' }}>
        <Text1 style={{ width: isMobile ? '86px' : '150px' }}>{index}</Text1>
        <Text1 style={{ flex: 1, whiteSpace: 'normal', wordBreak: 'keep-all', minHeight: pxToRem(88) }}><Trans i18nKey={description} /></Text1>
      </HorizontalLayout>
      <BTBox height={isMobile ? 16 : pxToRem(32)} />
    </VerticalLayout>
  );
};

type HistorySection2Props = {
  onFullyExpandedChange?: (isFull: boolean) => void; // ✅ 외부 콜백
};

const HistorySection2 = forwardRef<{ isFullyExpanded: () => boolean }, HistorySection2Props>(
  ({ onFullyExpandedChange }, ref) => {
    const { t } = useTranslation();
    const { horizontal: padding } = useResponsivePadding();
    const { isMobile } = useDeviceType();
    const lang = i18n.language as 'ko' | 'en';
    const [visibleSections, setVisibleSections] = useState(historyData.slice(0, 1));

    const isFull = visibleSections.length >= historyData.length;

    useImperativeHandle(ref, () => ({
      isFullyExpanded: () => isFull,
    }), [isFull]);

    useEffect(() => {
      onFullyExpandedChange?.(isFull);
    }, [isFull, onFullyExpandedChange]);

    useEffect(() => {
      setVisibleSections(historyData.slice(0, 1));
    }, []);

    useEffect(() => {
      setVisibleSections(historyData.slice(0, 1));
    }, [lang]);

    type HistoryLine = {
      index: string;
      description: string;
    };

    type HistoryItemParams = {
      year: string;
      imageSrc: string;
      lines: {
        index: string;
        description: string;
      }[];
      imagePosition?: 'left' | 'right'; // ✅ 기본값은 left
      yearMessage: string;
    };

    const createHistoryItem = ({ year, imageSrc, lines, imagePosition = 'left', yearMessage }: HistoryItemParams) => (
      imagePosition === 'left' ? (
        <AnimatedWrapper style={{ width: '100%', overflowX: 'hidden' }}>
          <HorizontalEqualLayout gap={pxToRem(55)} width={'100%'} style={{ alignItems: 'start' }}>
            <VerticalLayout>
              <BTBox height={pxToRem(159)} />
              <img
                src={imageSrc}
                style={{
                  width: '100%',
                  marginTop: '-30px',
                }}
              />
            </VerticalLayout>
            <VerticalLayout>
              <Data1>{year}</Data1>
              <BTBox height={pxToRem(24)} />
              <TitleNormalBold fontSize={pxToRem(28)} color='#979797'>{t(yearMessage)}</TitleNormalBold>
              <BTBox height={pxToRem(48)} />
              {lines.map((line, i) => (
                <LineItem key={i} index={line.index} description={line.description} />
              ))}
            </VerticalLayout>
          </HorizontalEqualLayout>
        </AnimatedWrapper>
      ) : (
        <AnimatedWrapper style={{ width: '100%', overflowX: 'hidden' }}>
          <HorizontalEqualLayout gap={55} width={'100%'} style={{ alignItems: 'start' }}>
            <VerticalLayout>
              <Data1>{year}</Data1>
              <BTBox height={pxToRem(24)} />
              <TitleNormalBold fontSize={pxToRem(28)} color='#979797'>{t(yearMessage)}</TitleNormalBold>
              <BTBox height={pxToRem(48)} />
              {lines.map((line, i) => (
                <LineItem key={i} index={line.index} description={line.description} />
              ))}
            </VerticalLayout>
            <VerticalLayout>
              <BTBox height={pxToRem(159)} />
              <img
                src={imageSrc}
                style={{
                  width: '100%',
                  marginTop: '-30px',
                }}
              />
            </VerticalLayout>
          </HorizontalEqualLayout>
        </AnimatedWrapper>
      )
    );

    const createHistoryItemMobile = ({ year, imageSrc, lines, imagePosition = 'left', yearMessage }: HistoryItemParams) => (
      <AnimatedWrapper style={{ width: '100%', overflowX: 'hidden' }}>
        <VerticalLayout width={'100%'}>
          <Data1>{year}</Data1>
          <BTBox height={8} />
          <TitleNormalBold fontSize={18} color='#979797'>{t(yearMessage)}</TitleNormalBold>
          <BTBox height={22} />
          <img
            src={imageSrc}
            style={{
              width: '100%',
            }}
          />
          <VerticalLayout style={{ flex: 1 }}>
            {lines.map((line, i) => (
              <LineItem key={i} index={line.index} description={line.description} showTopLine={i != 0} />
            ))}
          </VerticalLayout>
        </VerticalLayout>
      </AnimatedWrapper>
    );

    useEffect(() => {
      const handleScroll = () => {
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;
        const documentHeight = document.body.scrollHeight;

        if (scrollY + windowHeight >= documentHeight - 10) {
          if (visibleSections.length < historyData.length) {
            setTimeout(() => {
              const nextIndex = visibleSections.length;
              const nextItem = historyData[nextIndex];
              setVisibleSections([...visibleSections, nextItem]);
            }, 350);
          }
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [visibleSections]);

    return (
      <VerticalLayout paddingLeft={padding} paddingRight={padding} style={{ background: 'white' }}>
        <BTBox height={isMobile ? 40 : pxToRem(108)} />
        <AnimatedWrapper style={{ width: '100%', overflowX: 'hidden' }}>
          <H3>
            {t('기술로 써 내려온 믿음의 역사')}
          </H3>
        </AnimatedWrapper>
        {(visibleSections).map((item: HistoryItemParams) => (
          <React.Fragment key={item.year}>
            <BTBox height={isMobile ? 42 : pxToRem(100)} />
            {isMobile ? createHistoryItemMobile(item) : createHistoryItem(item)}
          </React.Fragment>
        ))}
        <BTBox height={isMobile ? 141 : pxToRem(296)} />
      </VerticalLayout>
    );
  });

export default HistorySection2;
